import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Download, Edit } from '@mui/icons-material';
import {
  useApiGet,
  useApiSend,
  getApiErrorMessage,
} from '../../utils/httpClientTs';
import {
  listFundAttachmentTypes,
  listFundAttachments,
  uploadFundAttachment,
  updateFundAttachment,
  deleteFundAttachment,
  downloadFundAttachment,
} from '../../endpoints/financeFunds';
import type {
  FundAttachmentGroup,
  FundAttachmentsGroupedResponse,
  FundAttachmentType,
} from '../../types/financeFunds';
import AlertItem from '../../Components/AlertItem';

interface FundAttachmentsTabProps {
  fundId: string;
  canManage: boolean;
}

const FundAttachmentsTab: React.FC<FundAttachmentsTabProps> = ({
  fundId,
  canManage,
}) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [uploadTypeId, setUploadTypeId] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [editTypeId, setEditTypeId] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: types = [] } = useApiGet<FundAttachmentType[]>(
    ['fund-attachment-types'],
    listFundAttachmentTypes,
    { enabled: true, retry: 1 }
  );

  const {
    data: grouped,
    isLoading,
    refetch,
  } = useApiGet<FundAttachmentsGroupedResponse, string>(
    ['fund-attachments', fundId],
    listFundAttachments,
    { enabled: !!fundId, retry: 1 },
    fundId
  );

  const invalidateKeys = [['fund-attachments', fundId]];

  const { mutateAsync: uploadMut, isPending: uploading } = useApiSend(
    (p: { file: File; typeId: string; description?: string }) =>
      uploadFundAttachment(fundId, p.file, p.typeId, p.description),
    () => {
      setUploadOpen(false);
      setUploadFile(null);
      setUploadTypeId('');
      setUploadDescription('');
      setActionError(null);
      void refetch();
    },
    (e: Error) => setActionError(getApiErrorMessage(e)),
    invalidateKeys
  );

  const { mutateAsync: patchMut, isPending: patching } = useApiSend(
    (p: { attachmentId: string; typeId: string; description: string }) =>
      updateFundAttachment(fundId, p.attachmentId, {
        attachment_type_id: p.typeId,
        description: p.description,
      }),
    () => {
      setEditId(null);
      setActionError(null);
      void refetch();
    },
    (e: Error) => setActionError(getApiErrorMessage(e)),
    invalidateKeys
  );

  const { mutate: deleteMut } = useApiSend(
    (attachmentId: string) => deleteFundAttachment(fundId, attachmentId),
    () => {
      setActionError(null);
      void refetch();
    },
    (e: Error) => setActionError(getApiErrorMessage(e)),
    invalidateKeys
  );

  const handleDownload = async (attachmentId: string, fileName: string) => {
    try {
      const { blob, filename } = await downloadFundAttachment(
        fundId,
        attachmentId
      );
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = filename || fileName || 'attachment';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setActionError(getApiErrorMessage(e));
    }
  };

  const openEdit = (group: FundAttachmentGroup, attachmentId: string) => {
    const item = group.attachments.find((a) => a.id === attachmentId);
    if (!item) return;
    setEditId(attachmentId);
    setEditTypeId(item.attachment_type_id);
    setEditDescription(item.description || '');
  };

  const groups = grouped?.groups ?? [];

  return (
    <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
      <CardHeader
        title="Attachments"
        action={
          canManage ? (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setActionError(null);
                setUploadOpen(true);
              }}
            >
              Add attachment
            </Button>
          ) : undefined
        }
      />
      <CardContent>
        {actionError && (
          <Box sx={{ mb: 2 }}>
            <AlertItem
              severity="error"
              description={actionError}
              onClose={() => setActionError(null)}
              keepOpen
            />
          </Box>
        )}
        {isLoading && <Typography>Loading attachments…</Typography>}
        {!isLoading && groups.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No attachments yet.
          </Typography>
        )}
        {groups.map((group) => (
          <Box key={group.type_id} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {group.type_name || 'Attachments'}
            </Typography>
            {group.attachments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No files in this category.
              </Typography>
            ) : (
              group.attachments.map((att) => (
                <Box
                  key={att.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{att.file_name}</Typography>
                    {att.description && (
                      <Typography variant="caption" color="text.secondary">
                        {att.description}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {att.uploaded_by_name || '—'}
                      {att.uploaded_at
                        ? ` · ${new Date(att.uploaded_at).toLocaleString()}`
                        : ''}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label="Download"
                    onClick={() => void handleDownload(att.id, att.file_name)}
                  >
                    <Download fontSize="small" />
                  </IconButton>
                  {canManage && (
                    <>
                      <IconButton
                        size="small"
                        aria-label="Edit"
                        onClick={() => openEdit(group, att.id)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Delete"
                        onClick={() => deleteMut(att.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              ))
            )}
          </Box>
        ))}
      </CardContent>

      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload attachment</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel required>Type</InputLabel>
            <Select
              label="Type"
              value={uploadTypeId}
              onChange={(e) => setUploadTypeId(String(e.target.value))}
            >
              {types.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Description"
            value={uploadDescription}
            onChange={(e) => setUploadDescription(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <Button variant="outlined" component="label">
            Choose file
            <input
              type="file"
              hidden
              onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          {uploadFile && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {uploadFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!uploadTypeId || !uploadFile || uploading}
            onClick={() => {
              if (!uploadFile || !uploadTypeId) return;
              void uploadMut({
                file: uploadFile,
                typeId: uploadTypeId,
                description: uploadDescription,
              });
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editId !== null}
        onClose={() => setEditId(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit attachment</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
            <InputLabel required>Type</InputLabel>
            <Select
              label="Type"
              value={editTypeId}
              onChange={(e) => setEditTypeId(String(e.target.value))}
            >
              {types.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditId(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!editId || !editTypeId || patching}
            onClick={() => {
              if (!editId) return;
              void patchMut({
                attachmentId: editId,
                typeId: editTypeId,
                description: editDescription,
              });
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default FundAttachmentsTab;
